// Source Catalog — every data pipeline this dashboard knows about.
// Status is what an engineer needs to ship the layer:
//   live      — adapter wired, endpoint working, no key required (or key shipped via env)
//   ready     — adapter scaffolded; flip a switch / drop a key to go live
//   planned   — known endpoint, no adapter yet; one afternoon of work
//   research  — we know the data exists but not the public endpoint
//
// Categories drive the "Source Catalog" panel groupings.

// "stub" — adapter file exists + returns mock data; waiting for a real data
//           source / partnership before going live
export type SourceStatus = "live" | "ready" | "planned" | "research" | "stub";

export type SourceCategory =
  | "mobility"      // traffic, transit, shuttle
  | "incidents"     // citizen reports, traffic events
  | "environment"   // weather, air, water, vegetation
  | "imagery"       // satellite, aerial, CCTV
  | "vibes"         // news, social, search trends
  | "infrastructure" // utilities, parking, energy
  | "maritime"      // AIS, ports, lighthouses, ferries
  | "open-data"     // data.go.th and adjacent national/local portals
  | "campus";       // legacy — kept for backward compat

export interface SourceEntry {
  id: string;
  label: string;
  vendor: string;
  category: SourceCategory;
  status: SourceStatus;
  endpoint?: string;     // URL or pattern
  apiPath?: string;      // path served by our API (when live)
  keyEnv?: string;       // env var name if a key is required
  pollSeconds?: number;
  describe: string;      // 1-line, engineer-readable
  docs?: string;
}

export const SOURCE_CATALOG: SourceEntry[] = [
  {
    id: "hii-rain-gauges", label: "HII Chonburi rain gauges via FloodDash", vendor: "HII + FloodDash",
    category: "environment", status: "live", endpoint: "https://flood.nonarkara.org/api/province/stations?code=20",
    apiPath: "/api/water/rain-gauges", pollSeconds: 600,
    describe: "Province-scoped observed 1h/24h rain; preserves source confidence, silent stations and real zero readings.",
  },
  {
    id: "rid-public-reservoirs", label: "RID Chonburi reservoir storage", vendor: "Royal Irrigation Department",
    category: "environment", status: "live", endpoint: "https://app.rid.go.th/reservoir/api/reservoir/public",
    apiPath: "/api/water/reservoirs", pollSeconds: 3600,
    describe: "RID's dated daily report for 12 Chonburi reservoirs, matched by name and conservation capacity. Storage, inflow and outflow give a net days-of-supply horizon; a filling dam has none.",
  },
  {
    id: "chonburi-open-data", label: "Chonburi provincial open-data inventory", vendor: "Chonburi GD Catalog",
    category: "open-data", status: "live", endpoint: "https://chonburi.gdcatalog.go.th/api/3/action/package_search",
    apiPath: "/api/open-data/catalog", pollSeconds: 3600,
    describe: "Complete paginated provincial catalog with original resource URLs, publishers, publication dates and licenses. Reference data, not live telemetry.",
  },
  {
    id: "hii-water-gauges", label: "ThaiWater / HII observed water levels", vendor: "HII + RID",
    category: "environment", status: "live", endpoint: "https://www.thaiwater.net/water/waterlevel",
    apiPath: "/api/water/gauges", pollSeconds: 600,
    describe: "Regional gauges with original observation times and HII situation classes; stale and missing values remain visible as unknown.",
  },
  {
    id: "dwr-cctv", label: "DWR canal snapshots", vendor: "Department of Water Resources",
    category: "imagery", status: "live", endpoint: "https://telemetry.dwr.go.th/reportCctv",
    apiPath: "/api/cctv/dwr", pollSeconds: 600,
    describe: "Na Kleo canal bridge (Sattahip) and Khlong Yai (Rayong); public snapshot service, capture timestamps and provider online flags.",
  },
  {
    id: "pattaya-official-cctv", label: "Pattaya official CCTV viewer", vendor: "Pattaya City",
    category: "imagery", status: "research", endpoint: "https://livestream.pattaya.go.th",
    describe: "Official viewer link only: terms restrict redistribution. No images or streams copied.", docs: "https://livestream.pattaya.go.th/terms",
  },
  // ---- Mobility ----
  {
    id: "itic-longdo",
    label: "iTIC / Longdo traffic events",
    vendor: "iTIC + Longdo Map",
    category: "mobility",
    status: "live",
    endpoint: "https://event.longdo.com/feed/json",
    apiPath: "/api/incidents/itic",
    pollSeconds: 180,
    describe: "Accidents, closures, construction, congestion events with lat/lng + severity.",
    docs: "https://map.longdo.com/products/traffic",
  },
  {
    id: "longdo-cameras",
    label: "Longdo CCTV cameras",
    vendor: "Longdo Map",
    category: "imagery",
    status: "ready",
    endpoint: "https://camera.longdo.com/feed/?command=json",
    apiPath: "/api/cctv/longdo",
    pollSeconds: 300,
    describe: "Eastern Seaboard camera locations; placeholder and suspended media are excluded. Catalog availability is not live-video verification.",
    docs: "https://camera.longdo.com",
  },
  {
    id: "bma-cctv",
    label: "BMA Traffic CCTV",
    vendor: "Bangkok Metropolitan Authority",
    category: "imagery",
    status: "research",
    endpoint: "https://www.bmatraffic.com/CCTV.aspx",
    describe: "BMA-operated traffic cameras; scrape catalog page or use ArcGIS feature service when located.",
    docs: "https://www.bmatraffic.com",
  },
  {
    id: "exat-cctv",
    label: "EXAT expressway cameras",
    vendor: "Expressway Authority of Thailand",
    category: "imagery",
    status: "research",
    endpoint: "https://www.exat.co.th/cctv/",
    describe: "Expressway cams; Rama IX / Sirat / Chalerm Maha Nakhon segments visible from Rama IV.",
  },
  {
    id: "highways-th",
    label: "Department of Highways cameras",
    vendor: "Thai Department of Highways",
    category: "imagery",
    status: "research",
    endpoint: "https://highway.dxc-thailand.com/",
    describe: "Highway 1/3/35 cameras; useful only if we extend the bbox out to expressway entrances.",
  },
  {
    id: "cu-shuttle",
    label: "CU Shuttle Bus (POP Bus) live",
    vendor: "Chulalongkorn University",
    category: "campus",
    status: "ready",
    endpoint: "https://www.cushuttlebus.com/api/live (TBD — confirm with CU IT)",
    apiPath: "/api/transit/cu-shuttle",
    keyEnv: "CU_SHUTTLE_TOKEN",
    pollSeconds: 30,
    describe: "5 free electric shuttle lines around campus; routes + stops shipped as GeoJSON, live positions need CU IT token.",
    docs: "https://www.chula.ac.th/about/green-university/cu-shuttle-bus/",
  },
  {
    id: "bts",
    label: "BTS Skytrain stations",
    vendor: "BTS Group",
    category: "mobility",
    status: "ready",
    apiPath: "/api/transit/bts",
    describe: "Static station GeoJSON (National Stadium, Siam, Ratchadamri are walking distance); arrivals require BTS API key.",
    docs: "https://www.bts.co.th/",
  },
  {
    id: "mrt",
    label: "MRT subway stations",
    vendor: "Bangkok Expressway and Metro",
    category: "mobility",
    status: "ready",
    apiPath: "/api/transit/mrt",
    describe: "Sam Yan, Hua Lamphong, Si Lom adjacent; static GeoJSON + GTFS for arrivals when feed key obtained.",
    docs: "https://metro.bemplc.co.th/",
  },
  {
    id: "bmta",
    label: "BMTA buses (GTFS)",
    vendor: "Bangkok Mass Transit Authority",
    category: "mobility",
    status: "planned",
    endpoint: "https://data.go.th/dataset/bmta-gtfs",
    describe: "Public GTFS feed via data.go.th; ingest stops within 800m of campus + route polylines.",
  },
  {
    id: "google-traffic",
    label: "Google Maps traffic raster",
    vendor: "Google",
    category: "mobility",
    status: "planned",
    keyEnv: "GOOGLE_MAPS_KEY",
    describe: "Tile overlay; commercial license required. Use as a fallback comparison to iTIC + simulated heatmap.",
  },
  {
    id: "tomtom-traffic",
    label: "TomTom traffic flow",
    vendor: "TomTom",
    category: "mobility",
    status: "planned",
    keyEnv: "TOMTOM_KEY",
    describe: "Free 2,500 req/day tier; per-segment speed vs. free-flow — good replacement for simulated heatmap.",
    docs: "https://developer.tomtom.com/traffic-api",
  },
  {
    id: "here-traffic",
    label: "HERE traffic flow + incidents",
    vendor: "HERE Technologies",
    category: "mobility",
    status: "planned",
    keyEnv: "HERE_API_KEY",
    describe: "Per-link jam factor JSON; free tier 250k req/month.",
    docs: "https://developer.here.com/",
  },

  // ---- Incidents ----
  {
    id: "traffy-fondue",
    label: "Traffy Fondue citizen reports",
    vendor: "NECTEC / BMA",
    category: "incidents",
    status: "live",
    endpoint: "https://publicapi.traffy.in.th/dump-csv-stat-bma/bangkok_traffy.json",
    apiPath: "/api/incidents/city-reports",
    pollSeconds: 300,
    describe: "Citizen complaint feed (potholes, flooding, waste, lighting); bbox-filtered to outer campus area.",
    docs: "https://www.traffy.in.th/",
  },
  {
    id: "city-reporter-v2",
    label: "City Reporter v2 (own intake)",
    vendor: "Dr Non — internal",
    category: "incidents",
    status: "planned",
    apiPath: "/api/incidents/internal",
    describe: "LINE/Telegram intake writing to Supabase; same shape as Traffy. Drop in when client wants their own channel.",
  },
  {
    id: "1555-bma",
    label: "BMA 1555 complaint line",
    vendor: "Bangkok Metropolitan Authority",
    category: "incidents",
    status: "research",
    describe: "Phone hotline; intake stats published as monthly PDF. No realtime API.",
  },

  // ---- Environment ----
  {
    id: "open-meteo",
    label: "Open-Meteo weather + forecast",
    vendor: "Open-Meteo",
    category: "environment",
    status: "live",
    endpoint: "https://api.open-meteo.com/v1/forecast",
    apiPath: "/api/weather",
    pollSeconds: 1800,
    describe: "No-key weather: current + 7d hourly. Used for KPI strip; also for rain prediction overlay later.",
  },
  {
    id: "open-meteo-aq",
    label: "Open-Meteo air quality grid",
    vendor: "Open-Meteo",
    category: "environment",
    status: "live",
    endpoint: "https://air-quality-api.open-meteo.com/v1/air-quality",
    apiPath: "/api/air-quality",
    pollSeconds: 900,
    describe: "US AQI + PM2.5/PM10 from Copernicus reanalysis grid. Free, no key.",
  },
  {
    id: "air4thai",
    label: "Air4Thai — PCD stations (Eastern Seaboard)",
    vendor: "Pollution Control Department",
    category: "environment",
    status: "live",
    endpoint: "http://air4thai.pcd.go.th/services/getNewAQI_JSON.php",
    apiPath: "/api/air-quality/air4thai",
    pollSeconds: 1800,
    describe: "Official Thai government AQ monitors across the Eastern Seaboard — Chonburi (Laem Chabang, Si Racha, Mueang) + Rayong / Map Ta Phut. Public, no key. Real PM2.5 + national AQI.",
    docs: "http://air4thai.pcd.go.th/",
  },
  {
    id: "waqi",
    label: "WAQI (World AQI) tiles",
    vendor: "WAQI / EBAS",
    category: "environment",
    status: "planned",
    keyEnv: "WAQI_TOKEN",
    endpoint: "https://api.waqi.info/v2/feed/here/?token={token}",
    describe: "Crowd-sourced AQI with tile overlay; token from waqi.info.",
  },
  {
    id: "openaq",
    label: "OpenAQ historical",
    vendor: "OpenAQ",
    category: "environment",
    status: "planned",
    endpoint: "https://api.openaq.org/v2",
    describe: "Historical air-quality time series; useful for hour-of-day baselines, not realtime.",
  },
  {
    id: "tmd-weather",
    label: "TMD Thai Meteorological Dept.",
    vendor: "TMD",
    category: "environment",
    status: "planned",
    endpoint: "https://data.tmd.go.th/api/Weather3Hours/v1/",
    keyEnv: "TMD_KEY",
    describe: "Free Thai-language forecast with 3-hour granularity. Auth required (register).",
  },
  {
    id: "bma-flood",
    label: "BMA flood + drainage",
    vendor: "BMA Drainage Dept.",
    category: "environment",
    status: "planned",
    endpoint: "https://weather.bangkok.go.th/Radar/",
    describe: "BMA flood gates, pump stations, weather radar. Used by Smart City Monitor; port adapter.",
  },
  {
    id: "gistda-flood",
    label: "GISTDA flood extent",
    vendor: "GISTDA",
    category: "environment",
    status: "planned",
    endpoint: "https://flood.gistda.or.th/",
    describe: "Sentinel-1 SAR-derived flood polygons during Thai monsoon. WMS endpoint available.",
  },

  // ---- Imagery ----
  {
    id: "gibs-true-color",
    label: "NASA GIBS true-color (MODIS)",
    vendor: "NASA EOSDIS",
    category: "imagery",
    status: "live",
    endpoint: "https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/MODIS_Terra_CorrectedReflectance_TrueColor/...",
    apiPath: "(client tile fetch)",
    describe: "1km true-color daily imagery; WMTS tile layer. Free, no key.",
    docs: "https://nasa-gibs.github.io/gibs-api-docs/",
  },
  {
    id: "gibs-ndvi",
    label: "NASA GIBS NDVI",
    vendor: "NASA EOSDIS",
    category: "imagery",
    status: "live",
    endpoint: "https://gibs.earthdata.nasa.gov/wmts/.../MODIS_Terra_NDVI_8Day/...",
    apiPath: "(client tile fetch)",
    describe: "Vegetation index 8-day composite. Useful for tracking campus greenery and heat-island gradient.",
  },
  {
    id: "sentinel-hub",
    label: "Sentinel Hub Process API",
    vendor: "ESA / Sinergise",
    category: "imagery",
    status: "planned",
    keyEnv: "SENTINEL_HUB_CLIENT_ID,SENTINEL_HUB_CLIENT_SECRET",
    endpoint: "https://services.sentinel-hub.com/api/v1/process",
    describe: "10m Sentinel-2 + Sentinel-1 SAR on demand. Free OAuth tier (30k PU/month).",
    docs: "https://docs.sentinel-hub.com/api/latest/",
  },
  {
    id: "gistda-theos",
    label: "GISTDA THEOS-2 / Pleiades",
    vendor: "GISTDA",
    category: "imagery",
    status: "planned",
    describe: "Thai high-res satellite; product order via dict.gistda.or.th, MOU needed for direct API.",
  },
  {
    id: "planetary-computer",
    label: "Microsoft Planetary Computer STAC",
    vendor: "Microsoft",
    category: "imagery",
    status: "planned",
    endpoint: "https://planetarycomputer.microsoft.com/api/stac/v1",
    describe: "STAC catalog + signed tile tokens; great for historical Sentinel/Landsat lookups.",
  },
  {
    id: "jaxa-himawari",
    label: "JAXA Himawari real-time",
    vendor: "JAXA / JMA",
    category: "imagery",
    status: "planned",
    endpoint: "https://www.eorc.jaxa.jp/ptree/index.html",
    describe: "Geostationary cloud loop, 10-min cadence. Use for storm approach.",
  },

  // ---- Vibes ----
  {
    id: "google-news-rss",
    label: "Google News RSS",
    vendor: "Google",
    category: "vibes",
    status: "live",
    endpoint: "https://news.google.com/rss/search?q=...",
    apiPath: "/api/news",
    pollSeconds: 300,
    describe: "Keyword-driven RSS (TH + EN) for Chula / Siam / Samyan / Henri Dunant.",
  },
  {
    id: "google-news-multilingual", label: "Google News multilingual desk", vendor: "Google + MyMemory",
    category: "vibes", status: "live", endpoint: "https://news.google.com/rss/search?q=...",
    apiPath: "/api/news/desk", pollSeconds: 900,
    describe: "Same-municipality coverage in 7 languages (TH/EN/JA/ZH/KO/DE/FR), machine-translated into Thai; the publisher's original is kept beside every translation.",
  },
  {
    id: "bangkok-post-rss",
    label: "Bangkok Post education RSS",
    vendor: "Bangkok Post",
    category: "vibes",
    status: "live",
    endpoint: "https://www.bangkokpost.com/rss/data/education.xml",
    apiPath: "/api/news",
    pollSeconds: 300,
    describe: "Higher-trust EN coverage; filtered to campus keywords inside our aggregator.",
  },
  {
    id: "thaipbs-rss",
    label: "Thai PBS English RSS",
    vendor: "Thai PBS",
    category: "vibes",
    status: "ready",
    endpoint: "https://www.thaipbsworld.com/feed/",
    describe: "Add to /api/news feed list.",
  },
  {
    id: "the-nation-rss",
    label: "The Nation RSS",
    vendor: "Nation Thailand",
    category: "vibes",
    status: "ready",
    endpoint: "https://www.nationthailand.com/rss",
    describe: "Add to /api/news feed list.",
  },
  {
    id: "talkwalker",
    label: "Talkwalker Free Alerts",
    vendor: "Talkwalker",
    category: "vibes",
    status: "planned",
    describe: "Email-driven alerts; route via webhook into news feed.",
  },
  {
    id: "reddit-rss",
    label: "Reddit r/Thailand + r/Bangkok",
    vendor: "Reddit",
    category: "vibes",
    status: "ready",
    endpoint: "https://www.reddit.com/r/Thailand+Bangkok/.rss",
    describe: "Public RSS; cap to keyword match around campus.",
  },
  {
    id: "google-trends",
    label: "Google Trends (campus terms)",
    vendor: "Google",
    category: "vibes",
    status: "planned",
    describe: "Daily/hourly trend deltas for 'Chula', 'Siam Square', 'Samyan'; needs serpapi or unofficial pytrends.",
  },

  // ---- BMA portals — POI + datasets ----
  {
    id: "bma-gis",
    label: "BMA GIS POIs (ArcGIS FeatureServer)",
    vendor: "Bangkok Metropolitan Authority",
    category: "infrastructure",
    status: "live",
    endpoint: "https://bmagis.bangkok.go.th/arcgis/rest/services",
    apiPath: "/api/bma/pois",
    pollSeconds: 3600,
    describe: "Hospitals, schools, fire/police stations, parks, markets, BMA offices, flood gates, pump stations, CCTV, bus stops — multi-layer ArcGIS, bbox-filtered.",
    docs: "https://bmagis.bangkok.go.th",
  },
  {
    id: "bma-data-portal",
    label: "BMA Open Data Portal (CKAN)",
    vendor: "Bangkok Metropolitan Authority",
    category: "infrastructure",
    status: "live",
    endpoint: "https://data.bangkok.go.th/api/3/action/package_search",
    apiPath: "/api/bma/datasets",
    pollSeconds: 21600,
    describe: "Keyword search across BMA-published datasets (hospitals, schools, fire stations, PM2.5, parks, waste, drainage, accidents).",
    docs: "https://data.bangkok.go.th/",
  },
  {
    id: "bma-citydata-portal",
    label: "BMA City Data Portal (CKAN, alt)",
    vendor: "Bangkok Metropolitan Authority",
    category: "infrastructure",
    status: "ready",
    endpoint: "https://citydataportal.bangkok.go.th/api/3/action/package_search",
    describe: "Secondary CKAN catalog (older portal). Some datasets only exist here; fall back when data.bangkok.go.th returns 0.",
  },
  {
    id: "pmcu-portfolio",
    label: "PMCU Property portfolio",
    vendor: "Property Management of Chulalongkorn University (PMCU)",
    category: "campus",
    status: "live",
    apiPath: "/geo/cu-lands.geojson",
    describe: "Full PMCU portfolio matched against OSM: Siamscape, Siam Square One, Samyan Mitrtown, Samyan Market, Chamchuri Square, MBK Center, Stadium One, Suphachalasai Stadium, Nimibutr Arena, Centenary Park, Thapthim Shrine, Chula Hospital. Under-construction Blocks 28/28X/33/34 are pending OSM authoring.",
    docs: "https://pmcu.co.th/",
  },
  {
    id: "pmcu-news",
    label: "PMCU announcements + projects",
    vendor: "Property Management of Chulalongkorn University (PMCU)",
    category: "campus",
    status: "research",
    endpoint: "https://pmcu.co.th/",
    describe: "Block 28X (Samyan young entrepreneurs), Block 33 (Suan Luang residential + wellness), Block 34 (planned), Samyan Smart City, Siam Square Walking Street. 1,153 rai (184 ha) total under management.",
    docs: "https://pmcu.co.th/",
  },
  {
    id: "cu-sustainability-kpis",
    label: "CU Sustainability KPIs",
    vendor: "Chulalongkorn University — Office of Sustainability",
    category: "campus",
    status: "research",
    endpoint: "https://sustainability.chula.ac.th/",
    describe: "Energy, water, waste, carbon, green-space KPIs published per development zone (Suan Luang–Sam Yan, Siam Square). Ingest as monthly time series.",
  },
  {
    id: "open-meteo-aq-trend",
    label: "Open-Meteo AQ 8h forecast (trend)",
    vendor: "Open-Meteo",
    category: "environment",
    status: "live",
    endpoint: "https://air-quality-api.open-meteo.com/v1/air-quality (hourly forecast)",
    apiPath: "/api/air-quality/trend",
    pollSeconds: 900,
    describe: "Drives the prominent AQI badge — current + next 8h US AQI + PM2.5 sparkline + advisory band.",
  },

  // ---- Infrastructure ----
  {
    id: "mea-outages",
    label: "MEA electricity outages",
    vendor: "Metropolitan Electricity Authority",
    category: "infrastructure",
    status: "planned",
    endpoint: "https://www.mea.or.th/profile/108",
    describe: "Scheduled outage notices; scrape into incident layer when timing intersects business hours.",
  },
  {
    id: "mwa-water",
    label: "MWA water outage notices",
    vendor: "Metropolitan Waterworks Authority",
    category: "infrastructure",
    status: "planned",
    endpoint: "https://www.mwa.co.th/article-detail/news-and-event/",
    describe: "Maintenance announcements affecting Pathum Wan district.",
  },
  {
    id: "cu-parking",
    label: "CU parking lot occupancy",
    vendor: "Chulalongkorn — Office of Property Management",
    category: "campus",
    status: "research",
    describe: "Smart parking pilot exists (sensors at some lots); need contact with OPM for data sharing.",
  },
  {
    id: "cu-energy",
    label: "CU green-university KPIs",
    vendor: "Chulalongkorn — Office of Sustainability",
    category: "campus",
    status: "research",
    endpoint: "https://www.chula.ac.th/about/green-university/",
    describe: "Energy, water, waste KPIs published in PDF; ingest as monthly time series.",
  },

  // ---- Global markets — FMP + FRED ----
  {
    id: "fmp-markets",
    label: "FMP — global indices, forex, commodities",
    vendor: "Financial Modeling Prep",
    category: "vibes",
    status: "live",
    endpoint: "https://financialmodelingprep.com/api/v3/quote/...",
    apiPath: "/api/markets",
    keyEnv: "FMP_API_KEY",
    pollSeconds: 600,
    describe: "Live ticks for SET Bangkok + global indices (S&P, Nikkei, Hang Seng, FTSE, DAX), THB forex pairs, and commodities (WTI, Brent, Gold, Silver). Free tier 250/day.",
    docs: "https://site.financialmodelingprep.com/developer/docs",
  },
  {
    id: "fred-macro",
    label: "FRED — US macro + Thai baht series",
    vendor: "Federal Reserve Economic Data (St. Louis Fed)",
    category: "vibes",
    status: "live",
    endpoint: "https://api.stlouisfed.org/fred/series/observations?...",
    apiPath: "/api/markets",
    keyEnv: "FRED_API_KEY",
    pollSeconds: 600,
    describe: "Daily series: DGS10 (US 10Y yield), DGS2 (2Y), VIXCLS (volatility), DEXTHUS (THB/USD), DCOILWTICO (WTI). 120k calls/day.",
    docs: "https://fred.stlouisfed.org/docs/api/fred/",
  },

  // ---- Added 2026-05-17 from Perplexity / Chula sustainability research ----
  {
    id: "aqicn-bangkok",
    label: "AQICN Bangkok stations (real-time)",
    vendor: "World Air Quality Index Project",
    category: "environment",
    status: "ready",
    endpoint: "https://api.waqi.info/feed/{station}/?token={AQICN_TOKEN}",
    keyEnv: "AQICN_TOKEN",
    pollSeconds: 900,
    describe: "Authoritative Bangkok AQ — PCD/BMA station network + US embassy monitors. Register free at aqicn.org/data-platform/token.",
    docs: "https://aqicn.org/api/",
  },
  {
    id: "chula-bems",
    label: "Chula BEMS — building energy data",
    vendor: "Chulalongkorn — Office of Sustainability",
    category: "infrastructure",
    status: "research",
    endpoint: "https://sustainability.chula.ac.th/report/610/",
    describe: "Building Energy Management System data published in the open-data list. Building-level kWh, demand, peaks — needs IT outreach for live feed.",
    docs: "https://sustainability.chula.ac.th/",
  },
  {
    id: "chula-zero-waste",
    label: "Chula Zero Waste — collection + diversion",
    vendor: "Chulalongkorn — Chula Zero Waste",
    category: "infrastructure",
    status: "research",
    endpoint: "https://sustainability.chula.ac.th/report/610/",
    describe: "Waste collection volumes by category, recycling diversion rates, organic + e-waste streams. Published as Zero Waste reports; ingest as monthly series.",
    docs: "https://www.chula.ac.th/en/news/zero-waste/",
  },
  {
    id: "bma-flood-platform",
    label: "BMA flood-risk platform (complaints + rainfall + drainage)",
    vendor: "Bangkok Metropolitan Administration",
    category: "environment",
    status: "research",
    endpoint: "https://www.bangkok.go.th/sub-portal/flood",
    describe: "Citizen flood complaints + rainfall gauges + drainage performance + canal levels fused in one BMA platform. Used by city ops; we need the underlying API/CKAN dataset names.",
    docs: "https://civicdatalab.in/work/climateaction/bma-flood-risk-data-platform/",
  },
  {
    id: "chula-buildings-dataset",
    label: "Chula building-recognition dataset (CV)",
    vendor: "Chulalongkorn — Faculty of Engineering",
    category: "campus",
    status: "research",
    endpoint: "https://arxiv.org/abs/2512.05468",
    describe: "Academic CV dataset for Chula campus buildings — for AR wayfinding overlays or auto-tagging photo evidence.",
  },
  {
    id: "data-go-th-points",
    label: "data.go.th — curated POIs (Chonburi)",
    vendor: "DGA + curated",
    category: "open-data",
    status: "live",
    apiPath: "/api/datago/points",
    describe: "Curated geocoded POIs across health, education, religion, government, markets and police — sourced from data.go.th CKAN exports.",
    docs: "https://data.go.th/dataset",
  },
  {
    id: "data-go-th-datasets",
    label: "data.go.th — dataset index",
    vendor: "DGA (Digital Government Agency)",
    category: "open-data",
    status: "live",
    endpoint: "https://data.go.th/api/3/action/package_search",
    apiPath: "/api/datago/datasets",
    pollSeconds: 3600,
    describe: "16+ curated Chonburi-relevant CKAN datasets (MoPH, MoE, DLA, DRA, RTP, PCD, EEC, Marine Dept, TAT, NSO, MoTS) plus live CKAN search.",
    docs: "https://data.go.th/dataset",
  },
  // ---- Chonburi-specific government dashboards ----
  {
    id: "chonburi-bi",
    label: "Chonburi Provincial BI dashboard",
    vendor: "Chonburi Provincial Office",
    category: "open-data",
    status: "research",
    endpoint: "https://chonburi-bi.chon.go.th",
    describe: "Chonburi province's own BI dashboard. Public information + planning indicators. Behind redirect; auth flow needed to embed live.",
    docs: "https://chonburi-bi.chon.go.th",
  },
  {
    id: "citydata-chonburi",
    label: "CityData Chonburi — Smart City Data Platform",
    vendor: "depa Smart City Data Platform",
    category: "open-data",
    status: "research",
    endpoint: "https://www.citydata.in.th/chonburi/en/dashboard-public-en/",
    describe: "depa-hosted public dashboard for Chonburi. Tourism datasets + 30+ municipal indicators downloadable as CSV.",
    docs: "https://www.citydata.in.th/chonburi/en/dashboard-public-en/",
  },
  {
    id: "chonburi-nso",
    label: "Chonburi Provincial Statistics Office",
    vendor: "NSO Thailand · Chonburi branch",
    category: "open-data",
    status: "research",
    endpoint: "https://chonburi.nso.go.th",
    describe: "Provincial statistics: population, household income, labour, agriculture, manufacturing, education, public health. Updated quarterly.",
    docs: "https://chonburi.nso.go.th",
  },
  {
    id: "chonburi-gov-news",
    label: "Chonburi provincial government news",
    vendor: "Chonburi Provincial Office",
    category: "vibes",
    status: "research",
    endpoint: "https://www.chonburi.go.th/news_it/showList",
    describe: "Official provincial announcement page. Behind Cloudflare anti-bot — needs headless browser or API key to ingest.",
    docs: "https://www.chonburi.go.th",
  },
  {
    id: "dla-opendata",
    label: "DLA Open Data — local administration",
    vendor: "Department of Local Administration",
    category: "open-data",
    status: "research",
    endpoint: "https://opendata.dla.go.th",
    describe: "Local-government datasets nationwide: municipal budgets, staffing, project tracking, OSCC integration. CKAN-style portal.",
    docs: "https://opendata.dla.go.th",
  },
  {
    id: "nso-provincial",
    label: "NSO National Statistical Office",
    vendor: "Office of the Prime Minister",
    category: "open-data",
    status: "research",
    endpoint: "https://www.nso.go.th/nsoweb/official_stat/province/m",
    describe: "Official provincial statistics across Thailand — economy, social, environment, governance indicators.",
    docs: "https://www.nso.go.th/nsoweb/official_stat/province/m?set_lang=en",
  },
  {
    id: "aqicn-chonburi",
    label: "AQICN Chonburi station",
    vendor: "World Air Quality Index project",
    category: "environment",
    status: "live",
    keyEnv: "AQICN_TOKEN",
    endpoint: "https://aqicn.org/city/chon-buri/",
    apiPath: "/api/air-quality/aqicn",
    describe: "Real-time AQI from Thailand's Pollution Control Department nearest station. Cross-validates and supplements Open-Meteo AQ grid. Requires AQICN_TOKEN (free at aqicn.org/data-platform/token).",
    docs: "https://aqicn.org/api/",
  },
  {
    id: "open-dev-mekong",
    label: "Open Development Thailand (Mekong)",
    vendor: "ODT / Mekong data hub",
    category: "open-data",
    status: "research",
    endpoint: "https://data.laos.opendevelopmentmekong.net/en/organization/thailand-organization",
    describe: "Cross-border spatial + sectoral datasets covering Thailand including EEC and Chonburi-area land use, water, and demographics.",
    docs: "https://opendevelopmentmekong.net",
  },
  // ---- Social ----
  {
    id: "fb-mueangchonburi",
    label: "Facebook · Mueang Chonburi page",
    vendor: "Meta (page plugin embed)",
    category: "vibes",
    status: "ready",
    endpoint: "https://www.facebook.com/mueangchonburi",
    apiPath: "/api/social/facebook",
    keyEnv: "FACEBOOK_PAGE_TOKEN",
    pollSeconds: 600,
    describe: "Official municipality page. No-auth Meta iframe embed always works; set FACEBOOK_PAGE_TOKEN + FACEBOOK_PAGE_ID for richer server-side post data.",
    docs: "https://www.facebook.com/mueangchonburi",
  },
  // ---- Marine + Oceanography ----
  {
    id: "open-meteo-marine",
    label: "Open-Meteo Marine — sea state forecast",
    vendor: "Open-Meteo",
    category: "maritime",
    status: "live",
    endpoint: "https://marine-api.open-meteo.com/v1/marine",
    apiPath: "/api/marine",
    pollSeconds: 1800,
    describe: "Wave height + period + direction, swell, sea-surface temperature, ocean current. 3 km cell, hourly, 48 h forecast. Free, no key. Drives the small-boat go/no-go verdict + surge alert.",
    docs: "https://open-meteo.com/en/docs/marine-weather-api",
  },
  {
    id: "tmd-marine-warnings",
    label: "TMD marine + storm warnings",
    vendor: "Thai Meteorological Department",
    category: "maritime",
    status: "research",
    endpoint: "https://www.tmd.go.th/en/marine",
    describe: "Gulf of Thailand wind + wave + tropical storm warnings issued every 6 h. Scrape or wait for the TMD open-data API release.",
    docs: "https://www.tmd.go.th",
  },
  {
    id: "hd-tides",
    label: "Hydrographic Dept tide tables",
    vendor: "Royal Thai Navy Hydrographic Department",
    category: "maritime",
    status: "research",
    endpoint: "https://www.hydro.navy.mi.th/tide",
    describe: "Official tide predictions for Si Racha / Laem Chabang. PDF tables — needs OCR or harmonic constituents to ingest.",
    docs: "https://www.hydro.navy.mi.th",
  },
  {
    id: "global-fishing-watch",
    label: "Global Fishing Watch — vessel activity",
    vendor: "Global Fishing Watch (Oceana / SkyTruth / Google)",
    category: "maritime",
    status: "ready",
    endpoint: "https://api.globalfishingwatch.org/v3",
    keyEnv: "GFW_API_TOKEN",
    describe: "AIS-derived fishing-effort heatmap by vessel type. Free for non-commercial with token. Wire once Chonburi-area dataset is downloaded.",
    docs: "https://globalfishingwatch.org/our-apis/",
  },
  {
    id: "noaa-sst",
    label: "NOAA STAR sea surface temperature",
    vendor: "NOAA / NESDIS",
    category: "maritime",
    status: "research",
    endpoint: "https://www.star.nesdis.noaa.gov/socd/sst",
    describe: "Daily 5 km global SST. Already covered for Chonburi point via Open-Meteo Marine; this is the backup if Open-Meteo goes dark.",
    docs: "https://www.star.nesdis.noaa.gov/socd/sst",
  },
  {
    id: "chonburi-fisheries-static",
    label: "Chonburi fishing + aquaculture zones",
    vendor: "Hand-authored (replace with DOF GIS)",
    category: "maritime",
    status: "live",
    describe: "Five zone polygons: Ang Sila oyster, Bang Saen shrimp, Bang Phra mussel, Chonburi Bay artisanal, Koh Si Chang offshore. Click any zone for boat-count + yearly yield.",
    docs: "https://www4.fisheries.go.th/local/index.php/main/site/chonburi",
  },
  {
    id: "chonburi-flood-static",
    label: "Chonburi coastal flood-risk zones",
    vendor: "Hand-authored (replace with DPF GIS)",
    category: "environment",
    status: "live",
    describe: "Four polygons of historical flood-prone zones: Bang Pla Soi old shophouses, Bang Saen waterfront, Muang old-town drainage backflow, Kheha estate. Severity + household count on hover.",
    docs: "https://data.go.th/dataset/flood-prone-area",
  },
  // ---- Maritime ----
  {
    id: "openseamap",
    label: "OpenSeaMap nautical overlay",
    vendor: "OpenSeaMap (community)",
    category: "maritime",
    status: "live",
    endpoint: "https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png",
    describe: "Community nautical chart overlay — shipping lanes, depth contours, anchorages, mooring buoys, navigational marks. Tiles, no auth.",
    docs: "https://openseamap.org",
  },
  {
    id: "aisstream",
    label: "AISStream — live vessel positions",
    vendor: "AISStream.io",
    category: "maritime",
    status: "ready",
    endpoint: "wss://stream.aisstream.io/v0/stream",
    apiPath: "/api/maritime/ais",
    keyEnv: "AISSTREAM_TOKEN",
    pollSeconds: 60,
    describe: "Real-time AIS positions for cargo, tankers, fishing, passenger vessels in the Gulf of Thailand bbox. Free with a registration key.",
    docs: "https://aisstream.io",
  },
  {
    id: "osm-maritime-infra",
    label: "OSM maritime infrastructure",
    vendor: "OpenStreetMap",
    category: "maritime",
    status: "live",
    describe: "Ports + harbour landuse + piers (119) · ferry terminals (4) · lighthouses + beacons + buoys (9). Fetched via Overpass, refreshed on deploy.",
    docs: "https://www.openstreetmap.org",
  },
  // ── GISTDA (Geo-Informatics & Space Technology Development Agency) ──────

  {
    id: "gistda-poi-digital-twin",
    label: "GISTDA POI Digital Twin",
    vendor: "GISTDA",
    category: "open-data",
    status: "live",
    endpoint: "https://gistdaportal.gistda.or.th/arcgis/rest/services/POI_Map_Service/MapServer/0",
    keyEnv: "GISTDA_API_KEY",
    describe: "100K+ national POIs: government, schools, temples, hotels, restaurants, sports, hospitals. Official Thai+English names + road names + disability access. ArcGIS FeatureServer, no auth needed for read.",
    docs: "https://gistdaportal.gistda.or.th",
  },
  {
    id: "gistda-solar-chonburi",
    label: "GISTDA LOD2 Solar irradiance — Chonburi",
    vendor: "GISTDA",
    category: "environment",
    status: "live",
    endpoint: "https://gistdaportal.gistda.or.th/arcgis/rest/services/GISDTA_BLDGLOD2_Solar_TimeSlider_Chonburi_Month/MapServer",
    keyEnv: "GISTDA_API_KEY",
    describe: "Monthly solar irradiance (kWh/m²) per LOD2 building footprint in Chonburi. 12-month time-slider. Fields: sun_irr, roof type, building type, height, area. Enables rooftop solar potential mapping.",
    docs: "https://gistdaportal.gistda.or.th",
  },
  {
    id: "gistda-solar-laemchabang",
    label: "GISTDA LOD2 Solar — Laem Chabang",
    vendor: "GISTDA",
    category: "environment",
    status: "live",
    endpoint: "https://gistdaportal.gistda.or.th/arcgis/rest/services/GISDTA_BLDGLOD2_Solar_TimeSlider_Laemchabang_Month/MapServer",
    keyEnv: "GISTDA_API_KEY",
    describe: "Same LOD2 solar data for Laem Chabang port / EEC zone. Industrial solar potential near Thailand's largest container port.",
  },
  {
    id: "gistda-admin-bounds",
    label: "GISTDA Administrative Boundaries",
    vendor: "GISTDA",
    category: "open-data",
    status: "research",
    endpoint: "https://gistdaportal.gistda.or.th/arcgis/rest/services/ข้อมูลเขตการปกครอง/MapServer",
    keyEnv: "GISTDA_API_KEY",
    describe: "Official Thai admin boundaries: province, district (อำเภอ), tambon (ตำบล), village (หมู่บ้าน). 5 ArcGIS layers. Pending correct bbox/filter to return Chonburi sub-units.",
  },
  {
    id: "gistda-pm25-idw",
    label: "GISTDA PM2.5 / PM10 / CO2 / NOx IDW rasters",
    vendor: "GISTDA",
    category: "environment",
    status: "ready",
    endpoint: "https://gistdaportal.gistda.or.th/arcgis/rest/services/Idw_02_PM2_Index/MapServer",
    keyEnv: "GISTDA_API_KEY",
    describe: "Inverse Distance Weighted interpolated air-quality rasters for Thailand (PM1, PM2.5, PM4, PM10, CO2, NOx). Coverage centres on NE/central Thailand — verify extent before using for Chonburi.",
  },
  {
    id: "gistda-waste-2611",
    label: "GISTDA Waste Disposal Sites (2,611 nationwide)",
    vendor: "GISTDA",
    category: "open-data",
    status: "ready",
    endpoint: "https://gistdaportal.gistda.or.th/arcgis/rest/services/2611landfills/FeatureServer",
    keyEnv: "GISTDA_API_KEY",
    describe: "All 2,611 registered waste disposal / landfill sites in Thailand. Fields: site name, province, owner type, management method, area. Filter by province code when wiring Chonburi layer.",
  },
  {
    id: "gistda-illegal-race",
    label: "GISTDA Illegal Street Racing Hotspots",
    vendor: "GISTDA",
    category: "incidents",
    status: "research",
    endpoint: "https://gistdaportal.gistda.or.th/arcgis/rest/services/IllegalRace/FeatureServer",
    keyEnv: "GISTDA_API_KEY",
    describe: "Point data of known illegal street racing locations + incidents with date + intensity. Safety layer — mayor can target police deployment.",
  },
  {
    id: "gistda-theos2",
    label: "GISTDA THEOS-2 Satellite (50 cm resolution)",
    vendor: "GISTDA",
    category: "imagery",
    status: "ready",
    endpoint: "https://gistdaportal.gistda.or.th",
    keyEnv: "GISTDA_API_KEY",
    describe: "Thailand's own Earth observation satellite. 50 cm resolution. GISTDA_API_KEY enables tasking requests for Chonburi: coastal flood monitoring, port surveillance, agricultural mapping, disaster response.",
    docs: "https://www.gistda.or.th/theos-2",
  },
  {
    id: "gistda-lst",
    label: "GISTDA Land Surface Temperature (CCO)",
    vendor: "GISTDA",
    category: "environment",
    status: "research",
    endpoint: "https://gistdaportal.gistda.or.th/arcgis/rest/services/LST_CCO_tif/MapServer",
    keyEnv: "GISTDA_API_KEY",
    describe: "Land Surface Temperature raster from Landsat/THEOS — Climate Change Observatory series. Urban heat island mapping for Laem Chabang + EEC industrial zones.",
  },
  {
    id: "gistda-unl",
    label: "GISTDA UNL (Thai Address Geocoding)",
    vendor: "GISTDA",
    category: "open-data",
    status: "research",
    keyEnv: "GISTDA_API_KEY",
    describe: "GISTDA's Thai address geocoding service — convert Thai addresses to lat/lng coordinates. Endpoint under investigation; API key format may differ from ArcGIS services.",
    docs: "https://www.gistda.or.th",
  },

  // ── Stub adapters — mock data; real API pending ──────────────────────────

  {
    id: "coastal-water-quality",
    label: "Coastal water quality — Chonburi stations",
    vendor: "PCD / DMCR (pending)",
    category: "environment",
    status: "stub",
    apiPath: "/api/environment/water-quality",
    describe:
      "DO₂, salinity, turbidity, pH at Bang Saen, Ang Sila, Bang Phra stations. " +
      "PCD marine water quality data is CSV-only on data.go.th (annual); real-time " +
      "sensor feed requires partnership with PCD or DMCR. Currently returns mock data.",
    docs: "https://data.go.th/dataset/402c771a-4b35-4dd7-aca8-d222fab61a49",
  },
  {
    id: "port-ops-laemchabang",
    label: "Laem Chabang port operations",
    vendor: "PAT (pending)",
    category: "maritime",
    status: "stub",
    apiPath: "/api/maritime/port-ops",
    describe:
      "Vessel count, berth utilisation, TEU throughput for Laem Chabang (THLCH) " +
      "and Chonburi town pier (THCBI). PAT has no public JSON API; real data " +
      "requires data-sharing agreement. AIS bbox proxy feasible as interim solution.",
    docs: "https://www.port.co.th",
  },

];

export function sourcesByStatus(status: SourceStatus) {
  return SOURCE_CATALOG.filter((s) => s.status === status);
}

export function sourcesByCategory(category: SourceCategory) {
  return SOURCE_CATALOG.filter((s) => s.category === category);
}

/**
 * How a feed names itself to a reader.
 *
 * A feed's machine id ("harmonic-tide-si-racha", "itic-longdo") tells an
 * engineer which adapter answered. It tells a mayor nothing they can act on.
 * The mayor brief is forwarded to LINE as text, so the source printed there
 * has to name the authority a mayor can phone.
 *
 * Unknown ids fall back to the id itself — a wrong name would be worse than
 * a technical one.
 */
const SOURCE_NAMES: Record<string, string> = {
  "rid-public-reservoirs": "RID",
  "datago-reservoirs": "Chonburi open data",
  "traffy-fondue": "Traffy Fondue",
  "itic-longdo": "iTIC",
  "open-meteo-minutely": "Open-Meteo",
  "open-meteo-marine": "Open-Meteo",
  "open-meteo-weather": "Open-Meteo",
  "open-meteo-air-quality": "Open-Meteo",
  "open-meteo-air-quality-trend": "Open-Meteo",
  "harmonic-tide-si-racha": "Approximate harmonic tide model",
  "hii-rain-via-flooddash": "HII / FloodDash",
  "hii-thaiwater-waterlevel": "ThaiWater / HII",
  "hii-waterlevel-via-flooddash": "HII / FloodDash",
  "pcd-air4thai": "PCD Air4Thai",
  "air4thai-pcd": "PCD Air4Thai",
  "aqicn": "AQICN",
  "google-news-multilingual": "Google News multilingual desk",
};

/**
 * Some adapters name the source per row rather than per feed — the PCD air
 * stations, for instance, arrive as "air4thai:61". The prefix names the
 * authority; the id after it is the station.
 */
const SOURCE_PREFIX_NAMES: [string, string][] = [
  ["air4thai:", "PCD Air4Thai"],
];

export function sourceName(id: string | undefined | null): string {
  if (!id) return "—";
  const exact = SOURCE_NAMES[id];
  if (exact) return exact;
  const prefixed = SOURCE_PREFIX_NAMES.find(([prefix]) => id.startsWith(prefix));
  if (prefixed) return prefixed[1];
  return id;
}
